import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { ENV } from '../../env';
import { authService } from '../../auth/services/authService';
import { tokenStorage } from '../../auth/utils/tokenStorage';

export class ApiError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

const apiClient = axios.create({
  baseURL: ENV.API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// Deduplicate identical in-flight GET requests (common with React StrictMode re-mounts).
const inFlightGetRequests = new Map<string, Promise<AxiosResponse<unknown>>>();

function sortObject(value: unknown): unknown {
  if (value instanceof URLSearchParams) return Array.from(value.entries());
  if (Array.isArray(value)) return value.map(sortObject);
  if (!value || typeof value !== 'object') return value;
  const sorted: Record<string, unknown> = {};
  Object.keys(value as Record<string, unknown>)
    .sort()
    .forEach((key) => {
      sorted[key] = sortObject((value as Record<string, unknown>)[key]);
    });
  return sorted;
}

function stableSerialize(value: unknown): string {
  return JSON.stringify(sortObject(value));
}

function buildGetRequestKey(url: string, config?: AxiosRequestConfig): string {
  const baseUrl = config?.baseURL ?? apiClient.defaults.baseURL ?? '';
  const params = config?.params ?? null;
  return `${baseUrl}|${url}|${stableSerialize(params)}`;
}

const originalGet = apiClient.get.bind(apiClient);

apiClient.get = ((url: string, config?: AxiosRequestConfig) => {
  const key = buildGetRequestKey(url, config);
  const existing = inFlightGetRequests.get(key);
  if (existing) return existing as Promise<AxiosResponse<unknown>>;

  const request = (originalGet(url, config) as Promise<AxiosResponse<unknown>>).finally(() => {
    inFlightGetRequests.delete(key);
  });

  inFlightGetRequests.set(key, request);
  return request;
}) as typeof apiClient.get;

function applyBearerToken(config: InternalAxiosRequestConfig, token: string): InternalAxiosRequestConfig {
  const headers =
    config.headers && typeof (config.headers as AxiosHeaders).set === 'function'
      ? (config.headers as AxiosHeaders)
      : new AxiosHeaders(config.headers as Record<string, string> | undefined);

  headers.set('Authorization', `Bearer ${token}`);
  config.headers = headers;
  return config;
}

// ---------------------------------------------------------------------------
// Request interceptor — send the current JWT with every API call.
// ---------------------------------------------------------------------------
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = tokenStorage.getAccessToken();
    if (!accessToken) return config;
    return applyBearerToken(config, accessToken);
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — normalise errors and auto-refresh expired tokens.
// ---------------------------------------------------------------------------
let isRefreshing = false;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ detail?: string | { msg: string }[]; message?: string }>) => {
    const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        tokenStorage.clear();
        authService.logout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return Promise.reject(error);
      }

      isRefreshing = true;

      try {
        const data = await authService.refreshToken(refreshToken);
        tokenStorage.setTokens(data.access_token, data.refresh_token, data.expires_in);

        originalRequest._retry = true;
        applyBearerToken(originalRequest as InternalAxiosRequestConfig, data.access_token);
        return apiClient(originalRequest as InternalAxiosRequestConfig);
      } catch (refreshError) {
        tokenStorage.clear();
        authService.logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const serverMessage =
      error.response?.data?.detail ??
      error.response?.data?.message ??
      error.message ??
      'An unexpected error occurred';
    return Promise.reject(new ApiError(
      Array.isArray(serverMessage) ? serverMessage.map(detail => detail.msg).join('; ') : serverMessage,
      error.response?.status,
    ));
  },
);

export default apiClient;
